'use client';
// biome-ignore lint/style/useImportType: wrong warning
import React from 'react';
import { forwardRef, useRef } from 'react';
import { buildRegularSource, buildWebpSource, priorityProp, } from '../SRCImage/utils.js';
import { useInView } from './useInView.js';
import { absolutePositioning, isIntersectionObserverAvailable, isSsr, universalBtoa, useImageLoad, useMergedRef, } from './utils.js';
const imageAddStrategy = ({ priority, inView, loaded }) => {
    if (priority) {
        return true;
    }
    if (isSsr) {
        return false;
    }
    if (isIntersectionObserverAvailable) {
        return inView || loaded;
    }
    return true;
};
const imageShowStrategy = ({ priority, loaded }) => {
    if (priority) {
        return true;
    }
    if (isSsr) {
        return false;
    }
    if (isIntersectionObserverAvailable) {
        return loaded;
    }
    return true;
};
export const Image = forwardRef(({ className, fadeInDuration = 500, intersectionTreshold, intersectionThreshold, intersectionMargin, pictureClassName, imgClassName, style, pictureStyle, imgStyle, layout = 'intrinsic', objectFit, objectPosition, data, onLoad, usePlaceholder = true, priority = false, sizes, srcSetCandidates = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4], placeholderClassName, placeholderStyle, }, ref) => {
    var _a, _b, _c, _d, _e;
    const imageRef = useRef(null);
    const [loaded, handleLoad] = useImageLoad(imageRef, onLoad);
    const [viewRef, inView] = useInView({
        threshold: intersectionThreshold || intersectionTreshold || 0,
        rootMargin: intersectionMargin || '0px 0px 0px 0px',
        triggerOnce: true,
        fallbackInView: true,
    });
    const rootRef = useMergedRef(ref, viewRef);
    const addImage = imageAddStrategy({ priority, inView, loaded });
    const showImage = imageShowStrategy({ priority, inView, loaded });
    const webpSource = buildWebpSource(data, sizes);
    const regularSource = buildRegularSource(data, sizes, srcSetCandidates);
    const transition = fadeInDuration > 0 ? `opacity ${fadeInDuration}ms` : undefined;
    const basePlaceholderStyle = Object.assign({ transition, opacity: showImage ? 0 : 1, 
        // During the opacity transition of the placeholder to the definitive version,
        // hardware acceleration is triggered. This results in the browser trying to render the
        // placeholder with your GPU, causing blurred edges. Solution: style the placeholder
        // so the edges overflow the container
        position: 'absolute', left: '-5%', top: '-5%', width: '110%', height: data.base64 ? 'auto' : '110%', maxWidth: 'none', maxHeight: 'none' }, placeholderStyle);
    const placeholder = usePlaceholder && data.base64 ? (React.createElement("img", { "aria-hidden": "true", alt: "", src: data.base64, className: placeholderClassName, style: Object.assign({ objectFit,
            objectPosition }, basePlaceholderStyle) })) : usePlaceholder && data.bgColor ? (React.createElement("div", { className: placeholderClassName, style: Object.assign({ backgroundColor: data.bgColor }, basePlaceholderStyle) })) : null;
    const { width, aspectRatio } = data;
    const height = (_a = data.height) !== null && _a !== void 0 ? _a : (aspectRatio ? width / aspectRatio : 0);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"></svg>`;
    const sizer = layout !== 'fill' ? (React.createElement("img", { className: imgClassName, style: Object.assign({ display: 'block', width: '100%' }, imgStyle), src: `data:image/svg+xml;base64,${universalBtoa(svg)}`, "aria-hidden": "true", alt: "" })) : null;
    return (React.createElement("div", { ref: rootRef, className: className, style: Object.assign(Object.assign({ overflow: 'hidden' }, (layout === 'fill'
            ? absolutePositioning
            : layout === 'intrinsic'
                ? { position: 'relative', width: '100%', maxWidth: width }
                : layout === 'fixed'
                    ? { position: 'relative', width }
                    : { position: 'relative', width: '100%' })), style) },
        sizer,
        placeholder,
        addImage && (React.createElement("picture", { className: pictureClassName, style: pictureStyle },
            webpSource,
            regularSource,
            data.src && (
            // biome-ignore lint/a11y/useAltText: We do support the `alt` attribute
            React.createElement("img", Object.assign({ ref: imageRef, src: data.src, alt: (_b = data.alt) !== null && _b !== void 0 ? _b : '', title: (_c = data.title) !== null && _c !== void 0 ? _c : undefined, onLoad: handleLoad }, priorityProp(priority ? 'high' : undefined), { className: imgClassName, style: Object.assign(Object.assign(Object.assign({ opacity: showImage ? 1 : 0, transition }, absolutePositioning), { objectFit,
                    objectPosition }), imgStyle) }))))),
        React.createElement("noscript", null,
            React.createElement("picture", { className: pictureClassName, style: pictureStyle },
                webpSource,
                regularSource,
                data.src && (
                // biome-ignore lint/a11y/useAltText: We do support the `alt` attribute
                React.createElement("img", Object.assign({ src: data.src, alt: (_d = data.alt) !== null && _d !== void 0 ? _d : '', title: (_e = data.title) !== null && _e !== void 0 ? _e : undefined, className: imgClassName, style: Object.assign(Object.assign(Object.assign({}, absolutePositioning), { objectFit,
                        objectPosition }), imgStyle), loading: priority ? undefined : 'lazy' }, priorityProp(priority ? 'high' : undefined))))))));
});
//# sourceMappingURL=index.js.map